<template>
  <div class="chat-actions">
    <div v-if="replyMessage" class="reply-message" data-external="chat-reply-message">
      <div class="reply-content">
        <FriedH5> {{ t('replying') }} {{ replyHeader }} </FriedH5>
        <FriedParagraph
          v-tooltip="{
            content: replyMessage.text,
            delay: { show: 300, hide: 100 },
            distance: 8,
          }"
          :size="ParagraphSize.Small"
          :data-external="`chat-reply-message-content:${replyMessage.text}`"
          >{{ replyMessage.text }}</FriedParagraph
        >
      </div>
      <div>
        <FriedIcon
          class="clear-reply-button"
          data-external="chat-actions-clear-reply-button"
          icon="close-circle"
          :size="IconSize.Small"
          @click="handleClearReply"
        />
      </div>
    </div>
    <div class="actions-container chat-container">
      <div class="text-input-container">
        <textarea
          ref="inputRef"
          v-model="text"
          rows="1"
          class="text-input"
          data-external="chat-text-area-input-field"
          :placeholder="t('write-something').toString()"
          @input="handleInput"
          @keydown.esc="resetInlineEmojiMenu"
          @keydown.left="resetInlineEmojiMenu"
          @keydown.right="resetInlineEmojiMenu"
          @keydown.up="handleNavigate"
          @keydown.down="handleNavigate"
          @keydown.tab="handleTab"
          @keydown.meta.enter="handleSend"
          @keydown.ctrl.enter="handleSend"
        ></textarea>
      </div>
      <div class="actions">
        <FriedFloater
          v-if="emojiIndex"
          :append-to-body="true"
          class="emoji-wrapper"
          :main-axis-offset="16"
          :cross-axis-offset="2"
          :show="showEmojis"
          :placement="Placement.TopStart"
        >
          <template #reference>
            <div
              v-wave
              data-external="chat-text-area-emoji-picker-button"
              :aria-label="t('show-emoji-picker')"
              :class="['emoji-button', { active: showEmojis }]"
              @click="setShowEmojiPicker(!showEmojis)"
            >
              <FriedIcon class="action-icon emoji-button-icon" icon="smiley" />
            </div>
          </template>
          <FriedEmojiPicker
            v-click-outside="clickOutside"
            :localization="localization"
            :emoji-index="emojiIndex"
            @select="handleAddEmoji"
            @skin-change="handleSkinChange"
          />
        </FriedFloater>
        <FriedButton
          ref="send"
          data-external="chat-text-area-send-message-button"
          :aria-label="t('send-message')"
          class="send-button"
          type="button"
          :disabled="!text"
          @click="handleSend"
        >
          {{ t('send') }}
          <template #rightIcon>
            <FriedIcon class="send-button-icon" :size="IconSize.Small" icon="send-arrow" />
          </template>
        </FriedButton>
      </div>
    </div>
    <div class="send-button-help-text">
      <template v-if="text">
        <FriedH5 data-testid="text-return">{{ t('return') }}&nbsp;</FriedH5>
        <FriedLabel>{{ t('dr_chat_to_add_a_new_line') }}</FriedLabel>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import type { ComputedRef, PropType, Ref } from 'vue';
import { IconSize, ParagraphSize, Placement } from '@getaccept/fried-tofu';
import { defineComponent, computed, onMounted, ref, nextTick, watch, onBeforeUnmount } from 'vue';
import type { EmojiIndex } from 'emoji-mart-vue-fast';
import { store } from 'emoji-mart-vue-fast';
import type { Emoji } from '@getaccept/fried-tofu';
import { EmojiHelper } from '@getaccept/lib-shared/src/emoji/helpers/emoji.helper';
import { EMOJI_INLINE_TRIGGER_COUNT } from '@getaccept/lib-shared/src/emoji/constants/emoji.constants';
import { useI18n } from 'vue-i18n';
import useBreakpoints from '@getaccept/lib-shared/src/composables/breakpoints.composable';
import { useSessionStorage } from '@vueuse/core';
import ChatHelper from '../helpers/chat.helper';
import ChatConstants from '../constants/chat-constants';
import type { Conversation, Message, MessageDraft } from '../types';
import type { Participant } from '../../types';
import AvatarHelper from '../../helpers/avatar.helper';

export default defineComponent({
  name: 'ChatActions',
  props: {
    replyMessage: {
      type: Object as PropType<Message>,
      default: undefined,
    },
    participants: {
      type: Array as PropType<Array<Participant>>,
      default: () => [],
    },
    conversation: {
      type: Object as PropType<Conversation>,
      default: undefined,
    },
    creatingNewConversation: {
      type: Boolean,
    },
    draftStorageKey: {
      type: String,
      required: true,
    },
  },
  emits: ['send', 'typing', 'clear-reply'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const inputRef: Ref<HTMLTextAreaElement> = ref(null);
    const emojiIndex = ref<EmojiIndex>(null);
    const clearReplyEmit = 'clear-reply';
    const draftMessages = useSessionStorage<MessageDraft[]>(props.draftStorageKey, []);

    const text = ref('');
    const skinTone = ref(0);
    const emojiSearch = ref('');
    const selectedInlineEmojiIndex = ref(0);
    const showSendButtonTooltip = ref(false);
    const showEmojiButtonTooltip = ref(false);
    const showEmojis = ref(false);
    const showInlineEmojiMenu = ref(false);
    const isTyping = ref(false);
    const localization = ref(ChatHelper.getEmojiLocalization());

    const matchedEmojis = computed(() =>
      EmojiHelper.getEmojiListByKey(emojiSearch.value, emojiIndex.value)
    );

    const correctEmojiSearch = computed(
      () =>
        emojiSearch.value.charAt(0) === ':' &&
        emojiSearch.value.length >= EMOJI_INLINE_TRIGGER_COUNT &&
        matchedEmojis.value.length > 0 &&
        !emojiSearch.value.includes(' ')
    );

    const handleSkinChange = (newSkintone: number): void => {
      skinTone.value = newSkintone;
    };

    const currentDraft = computed(() =>
      ChatHelper.getDraftMessage(draftMessages.value, props.conversation?.id)
    );

    const getEmojiKey = (element: HTMLTextAreaElement): string => {
      const end: number = element?.selectionEnd;
      const start: number = text.value.lastIndexOf(':', end - 2);
      return start >= 0 ? text.value.substring(start, end) : '';
    };

    const setShowInlineEmojiMenu = (show: boolean): void => {
      showInlineEmojiMenu.value = show;
    };

    const resetInlineEmojiMenu = (): void => {
      setShowInlineEmojiMenu(false);
      emojiSearch.value = '';
      selectedInlineEmojiIndex.value = 0;
    };

    const resetInput = () => {
      text.value = currentDraft.value;
      isTyping.value = false;
      emit(clearReplyEmit);
      inputRef.value?.focus();
    };

    const replaceEmojiSearchWithEmoji = async (emoji: Emoji): Promise<void> => {
      const nativeEmoji: string = EmojiHelper.getEmojiWithSkinTone(emoji, skinTone.value)?.native;
      if (!nativeEmoji) {
        resetInlineEmojiMenu();
        return;
      }
      const cursorPosition: number =
        inputRef.value?.selectionEnd - emojiSearch.value.length + nativeEmoji.length;
      text.value = text.value.replace(emojiSearch.value, nativeEmoji);
      resetInlineEmojiMenu();
      if (inputRef.value?.setSelectionRange) {
        await nextTick();
        inputRef.value?.setSelectionRange(cursorPosition, cursorPosition);
      }
    };

    const setShowEmojiPicker = (show: boolean): void => {
      showEmojis.value = show;
    };

    const handleAddEmoji = async (emoji: Emoji): Promise<void> => {
      const cursorPosition: number = inputRef.value?.selectionEnd;
      setShowEmojiPicker(false);
      text.value =
        text.value.slice(0, cursorPosition) + emoji.native + text.value.slice(cursorPosition);
      inputRef.value?.focus();
      await nextTick();

      if (inputRef.value?.setSelectionRange) {
        inputRef.value?.setSelectionRange(
          cursorPosition + emoji.native.length,
          cursorPosition + emoji.native.length
        );
      }
    };

    const resizeTextarea = () => {
      inputRef.value.style.height = 'auto';
      inputRef.value.style.height = `${inputRef.value.scrollHeight}px`;
    };

    const updateDraftMessages = (conversationId: string, text: string) => {
      draftMessages.value = ChatHelper.updateDraftMessages(
        [...draftMessages.value],
        conversationId,
        text
      );
    };

    const handleSend = async () => {
      inputRef.value?.focus();
      if (text.value) {
        emit('send', text.value);
        text.value = '';
        await nextTick();
        resizeTextarea();
        if (!props.creatingNewConversation) {
          updateDraftMessages(props.conversation.id, text.value);
        }
      }
    };

    const clickOutside = (): void => {
      if (!showEmojis.value) {
        return;
      }
      setShowEmojiPicker(false);
    };

    const handleTab = (event: KeyboardEvent): void => {
      if (showInlineEmojiMenu.value) {
        event.preventDefault();
        replaceEmojiSearchWithEmoji(matchedEmojis.value[selectedInlineEmojiIndex.value]);
      }
    };

    const handleNavigate = (event: KeyboardEvent): void => {
      if (!showInlineEmojiMenu.value) {
        return;
      }
      event.preventDefault();
      const firstSelected: boolean = selectedInlineEmojiIndex.value === 0;
      const lastSelected: boolean =
        selectedInlineEmojiIndex.value === matchedEmojis.value.length - 1;

      let step: number;
      switch (event.key) {
        case 'ArrowUp':
          if (firstSelected) {
            return;
          }
          step = -1;
          break;
        case 'ArrowDown':
          if (lastSelected) {
            return;
          }
          step = 1;
          break;
      }

      selectedInlineEmojiIndex.value += step;
    };

    let resetTypingStatus: ReturnType<typeof setTimeout>;

    const handleTypingStatus = (typingStatus: boolean) => {
      if (typingStatus === isTyping.value) {
        return;
      }
      isTyping.value = typingStatus;
      emit('typing', typingStatus);
    };

    watch(
      () => text.value,
      (newValue: string) => {
        if (newValue === currentDraft.value) {
          return handleTypingStatus(false);
        }
        if (!isTyping.value) {
          handleTypingStatus(!!newValue);
        }

        clearTimeout(resetTypingStatus);
        resetTypingStatus = setTimeout(
          () => handleTypingStatus(false),
          ChatConstants.typingStatusTimeout
        );
      },
      { deep: true }
    );

    const handleInput = (): void => {
      resizeTextarea();
      emojiSearch.value = getEmojiKey(inputRef.value);
      const isEndingColon = text.value.charAt(text.value.length - 1) === ':';
      selectedInlineEmojiIndex.value = 0;

      if (isEndingColon && correctEmojiSearch.value) {
        const emoji = emojiIndex.value.findEmoji(emojiSearch.value);
        replaceEmojiSearchWithEmoji(emoji);
        return;
      }
      if (correctEmojiSearch.value && !showInlineEmojiMenu.value) {
        setShowInlineEmojiMenu(true);
        return;
      }
      if (!correctEmojiSearch.value) {
        resetInlineEmojiMenu();
      }
    };

    const replyHeader: ComputedRef<string> = computed(() => {
      const { participantId, userId } = props?.replyMessage?.owner || {};
      const ownerId = props?.replyMessage?.ownerId;

      if (!ownerId && !participantId && !userId) {
        return;
      }
      const participant = props.participants.find(
        ({ id, newParticipantId }) =>
          (ownerId && ownerId === newParticipantId) || id === participantId || id === userId
      );

      return AvatarHelper.getDisplayName(participant);
    });

    const handleClearReply = (): void => {
      emit(clearReplyEmit);
    };

    watch(
      () => props.replyMessage,
      (newValue: Message) => {
        if (newValue) {
          inputRef.value?.focus();
        }
      }
    );

    const { isSmallScreenAndDown } = useBreakpoints();

    const handleUnmount = () => {
      if (props.creatingNewConversation) {
        return;
      }
      const drafts = ChatHelper.updateDraftMessages(
        draftMessages.value,
        props.conversation.id,
        text.value
      );
      sessionStorage.setItem(props.draftStorageKey, JSON.stringify(drafts));
    };

    watch(
      () => props.conversation?.id,
      (newId: string, oldId: string) => {
        if (oldId && oldId !== newId) {
          updateDraftMessages(oldId, text.value);
        }
        if (!newId) {
          text.value = '';
          return;
        }
        resetInput();
      }
    );

    onMounted(async () => {
      emojiIndex.value = await EmojiHelper.emojiIndex();
      skinTone.value = store.get('skin');
      text.value = currentDraft.value;

      if (!isSmallScreenAndDown.value && !props.creatingNewConversation) {
        await nextTick();
        inputRef.value?.focus();
        resizeTextarea();
      }
      window.addEventListener('beforeunload', handleUnmount);
    });

    onBeforeUnmount(() => {
      handleUnmount();
      window.removeEventListener('beforeunload', handleUnmount);
    });

    return {
      text,
      skinTone,
      emojiSearch,
      selectedInlineEmojiIndex,
      showSendButtonTooltip,
      showEmojiButtonTooltip,
      showEmojis,
      showInlineEmojiMenu,
      isTyping,
      localization,
      inputRef,
      handleInput,
      handleSend,
      handleTab,
      handleNavigate,
      handleSkinChange,
      handleAddEmoji,
      matchedEmojis,
      clickOutside,
      setShowEmojiPicker,
      replaceEmojiSearchWithEmoji,
      resetInlineEmojiMenu,
      replyHeader,
      handleClearReply,
      IconSize,
      emojiIndex,
      handleTypingStatus,
      setShowInlineEmojiMenu,
      correctEmojiSearch,
      getEmojiKey,
      ParagraphSize,
      t,
      resizeTextarea,
      draftMessages,
      resetInput,
      Placement,
    };
  },
});
</script>

<style lang="scss" scoped>
.chat-actions {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  box-sizing: border-box;
  background-color: var(--white);
  padding: var(--spacing-150);
  padding-bottom: 0;

  @media screen and (max-width: $sm) {
    padding: var(--spacing-150) 0 0 0;
  }

  .reply-message {
    display: flex;
    justify-content: space-between;
    border-bottom: 1px solid var(--gray-94);
    background: var(--gray-98);
    padding: var(--spacing-100);
    width: 100%;
    max-width: 1024px;

    :first-child {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      > p {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    :last-child {
      align-self: center;
    }

    .reply-content {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-50);

      > h5,
      > p {
        color: var(--text-gray);
      }

      > p {
        align-self: flex-start;
        width: 100%;
      }
    }

    .reply-header {
      font-weight: bold;
      color: var(--gray-60);

      .replying {
        font-weight: normal;
      }
    }

    .reply-text {
      color: var(--text-gray);
    }

    .clear-reply-button {
      color: var(--base-blue);
      cursor: pointer;
    }
  }

  .actions-container {
    display: flex;
    flex-direction: row;
    height: 100%;

    .text-input-container {
      width: 100%;
      padding: var(--spacing-100) 0 var(--spacing-100) var(--spacing-100);

      .text-input {
        border: none;
        outline: none;
        box-shadow: none;
        resize: none;
        width: 100%;
        margin: 0;
        font-size: $paragraph-medium-font-size;
      }
    }

    .actions {
      display: flex;
      align-items: center;
      margin-top: 10px;
      margin-right: var(--spacing-75);

      .send-button,
      .emoji-button {
        &.is-preview {
          cursor: auto;
        }

        height: 2rem;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .send-button-icon {
        width: var(--spacing-100);
      }

      .emoji-button {
        width: 2rem;
        border-radius: 50%;
        cursor: pointer;

        &:hover {
          background-color: var(--gray-92);
        }
      }
    }

    &.chat-container {
      position: relative;
      display: flex;
      justify-content: space-between;
      align-items: center;
      align-self: center;
      width: 100%;
      max-width: 1024px;
      border-radius: var(--radius-medium);
      border: 1px solid var(--gray-92);
      background: var(--white);

      &:focus-within {
        border-color: var(--link-blue);
      }

      @media screen and (max-width: $sm) {
        border: 0;
        border-top: 1px solid var(--gray-92);
        border-radius: 0;
      }

      .text-input {
        background: none;
        max-height: 70vh;

        &::placeholder {
          color: var(--link-blue);
        }

        @media screen and (max-width: $xs) {
          margin-top: auto;
          max-height: 35vh;
        }
      }

      .actions {
        margin: calc(var(--spacing-100) + var(--spacing-25));

        .emoji-wrapper {
          margin-right: var(--spacing-25);
        }

        .emoji-button {
          color: var(--link-blue);
        }
      }
    }
  }
}

.send-button-help-text {
  max-width: 1024px;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  min-height: var(--spacing-150);

  @media screen and (max-width: $sm) {
    display: none;
  }

  .heading-5 {
    color: var(--gray-49);
  }

  .label {
    color: var(--gray-49);
  }
}
</style>
